import {
  Button,
  FlexBox,
  Icon,
  IconButton,
  Page,
  Table,
  Tag,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { isResponseWithData } from 'lib-shared-auth';
import capitalize from 'lodash/capitalize';
import moment from 'moment';
import { useMemo } from 'react';
import { useInterval } from 'react-use';

import { mimChatbotsApi } from 'apis/chatbots';
import {
  KnowledgeBase,
  knowledgeBasesApi,
  KnowledgeBaseType,
} from 'apis/knoledgeBases';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';

export const MimChatbotSourcesView = (): JSX.Element => {
  const notify = useNotifications();
  const dispatch = useAppDispatch();

  const flags = useFeatureFlag(['knowledge-access', 'website-data-source']);
  const knowledgeSource: KnowledgeBaseType[] = useMemo(
    () =>
      [
        'elevio',
        ...(flags.knowledgeAccess ? ['dixa_knowledge'] : []),
        ...(flags.websiteDataSource ? ['website'] : []),
      ] as KnowledgeBaseType[],
    [flags.knowledgeAccess, flags.websiteDataSource],
  );

  const { id } = useAppPathParams('/settings/chatbots/mim/:id/sources');

  const organizationId = useAppSelector(
    (state) => state.settings.organization.organization.id,
  );

  const [updateChatbot] = mimChatbotsApi.useUpdateMimChatbotSourcesMutation();

  const {
    chatbot,
    refetch,
    isLoading: isChatbotLoading,
    isError: isChatbotError,
  } = mimChatbotsApi.useReadMimChatbotByIdQuery(
    { id },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => {
        return {
          ...rest,
          chatbot: data?.data,
        };
      },
    },
  );
  const {
    knowledgeBases,
    isLoading: isDataSourceLoading,
    isError: isDataSourceError,
  } = knowledgeBasesApi.useReadKnowledgeBasesQuery(
    { organizationId: organizationId, source: knowledgeSource },
    {
      skip: !chatbot,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => {
        return {
          ...rest,
          knowledgeBases: data?.data.knowledgeBases
            ? [...data.data.knowledgeBases]
                .reverse()
                .filter((kb) =>
                  chatbot?.knowledgeBases.some((usedKb) => usedKb.id === kb.id),
                )
            : [],
        };
      },
    },
  );

  const isLoading = isChatbotLoading || isDataSourceLoading;
  const isError = isChatbotError || isDataSourceError;

  useInterval(
    () => refetch(),
    knowledgeBases.some((kb) => kb.state !== 'ready') ? 20000 : null,
  );

  const handleUpdateDataSources = async () => {
    await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:chatbotUpdateSourcesModal',
          arg: { organizationId, id },
        },
      }),
    );
  };

  const handleDeleteKnowledgeBase = async (knowledgeBase: KnowledgeBase) => {
    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Remove data source',
            description: `Remove "${
              knowledgeBase.name
            } from the data sources of "${
              chatbot!.name
            }"? The chatbot will no longer use this data source for finding solutions.`,
            confirm: {
              label: 'Remove data source',
              variant: 'primary',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        const response = await updateChatbot({
          id,
          name: chatbot!.name,
          knowledgeBases: chatbot!.knowledgeBases.filter(
            (kb) => kb.id !== knowledgeBase.id,
          ),
        }).unwrap();

        if (isResponseWithData(response)) {
          notify(
            {
              title: 'Success',
              text: `"${response.data?.name || 'Chatbot'}" has been updated.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );
        }
      } catch (error) {
        notify(
          {
            title: 'Error while updating chatbot.',
            text: 'Something went wrong.',
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    }
  };

  return (
    <Page>
      <Page.Section
        title="Data sources for this chatbot"
        controls={
          <Button
            variant="secondary"
            slotStart={<Icon name="plus" />}
            onClick={handleUpdateDataSources}
          >
            Connect existing data source
          </Button>
        }
      >
        <Table
          stickyHeader
          loading={isLoading}
          loadingFailed={isError}
          emptyState={
            isLoading !== true && knowledgeBases.length === 0
              ? {
                  title: 'Add data source',
                  description:
                    'Add your first data source to be able to use a Mim chatbot.',
                  buttonProps: {
                    onClick: handleUpdateDataSources,
                    children: 'Add data source',
                    variant: 'secondary',
                  },
                }
              : undefined
          }
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th columnId="name">Name</Table.Th>
              <Table.Th columnId="source">Source</Table.Th>
              <Table.Th columnId="synced">Last synced</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {knowledgeBases?.map((kb, index) => (
              <Table.Tr key={index}>
                <Table.Td>
                  <FlexBox gap="100">
                    <Tooltip content={capitalize(kb.state)} key={kb.id}>
                      <Tag
                        color={
                          kb.state === 'ready'
                            ? colors.green[400]
                            : colors.orange[400]
                        }
                      >
                        {kb.name}
                      </Tag>
                    </Tooltip>
                  </FlexBox>
                </Table.Td>
                <Table.Td>
                  {capitalize(kb.source.replace(/_/g, ' ') ?? '')}
                </Table.Td>
                <Table.Td>
                  {kb.synced_at
                    ? moment(kb.synced_at).format('DD/MM/YYYY HH:mm:ss')
                    : ''}
                </Table.Td>
                <Table.Td align="right">
                  {kb.state === 'ready' && (
                    <IconButton
                      variant="tertiary"
                      icon="trash"
                      onClick={() => handleDeleteKnowledgeBase(kb)}
                    />
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Page.Section>
    </Page>
  );
};
